"use client";

import { useState } from "react";
import { ArrowRight, Plus, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Faq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const faqs = [{
    question: "Wie lange dauert die Programmierung eines neuen Fahrzeugschlüssels?",
    answer: "In der Regel ist der Prozess innerhalb von 30 bis 60 Minuten abgeschlossen."
  }, {
    question: "Kann ein gebrauchter Fahrzeugschlüssel erneut programmiert werden?",
    answer: "Das hängt vom Fahrzeugmodell und dem Schlüsseltyp ab. Ob ein gebrauchter Fahrzeugschlüssel erneut programmiert werden kann, prüfen wir gerne vorab. Originale Fahrzeugschlüssel sind bei vielen Marken und Modellen nach der Programmierung gesperrt. Je nach Fahrzeug können diese wieder entsperrt und erneut verwendet werden – dies ist jedoch nicht bei allen Fahrzeugen möglich."
  }, {
    question: "Was benötige ich für die Anfertigung eines Ersatzschlüssels?",
    answer: "Vorab benötigen wir in der Regel den Fahrzeugschein sowie Informationen zum Fahrzeugschlüssel (z. B. Anzahl der Tasten und Keyless oder nicht). Zum Termin bringen Sie bitte das Fahrzeug und alle vorhandenen Fahrzeugschlüssel mit."
  }, {
    question: "Bieten Sie auch einen Vor-Ort-Service an?",
    answer: "Ja. Unser Schlüsselservice ist hauptsächlich als mobiler Vor-Ort-Service ausgelegt. Auf Wunsch kommen wir direkt zu Ihnen und programmieren sowie fräsen den Fahrzeugschlüssel vor Ort. Nach Absprache ist der Service auch an unserem Standort möglich.\n"
  }, {
    question: "Können Sie Schlüssel für alle Fahrzeugmarken programmieren?",
    answer: "Wir decken ein breites Spektrum an gängigen Fahrzeugmarken ab. Da sich die Technik ständig weiterentwickelt, erweitern wir unser Portfolio kontinuierlich. Kontaktieren Sie uns gerne für eine spezifische Anfrage zu Ihrem Modell."
  }, {
    question: "Können auch mitgebrachte Schlüssel oder\xA0 programmiert werden?\n",
    answer: "Ja, Je nach Fahrzeug und Schlüsseltyp können auch mitgebrachte Schlüssel oder Schlüsselrohlinge programmiert und angelernt werden. Voraussetzung ist, dass diese mit Ihrem Fahrzeug kompatibel sind."
  }, {
    question: "Was passiert, wenn einer meiner Fahrzeugschlüssel nicht mehr funktioniert?\n",
    answer: "Wir prüfen zunächst die Ursache des Defekts. Je nach Fahrzeug und Fehler kann der Schlüssel erneut programmiert, repariert oder bei Bedarf ersetzt werden."
  }, {
    question: "Sind weitere Dienstleistungen in Planung?",
    answer: "Ja, Unser Leistungsangebot wird kontinuierlich erweitert. Geplant sind weitere Dienstleistungen rund um Fahrzeugschlüssel, Steuergeräte und Fahrzeugdiagnose."
  }];
  return <section data-section-id="4379" id="häufige-fragen-zum-ersatzschlüssel" className="bg-background text-foreground py-32 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <p className="font-default text-xs uppercase tracking-[0.25em] mb-8" style={{
              color: "var(--chart-1)"
            }}>
                03 — Support
              </p>
              <h2 className="font-default text-5xl md:text-6xl font-semibold leading-[1.05] mb-8 tracking-tighter">
                Häufige <span className="italic font-normal">Fragen.</span>
              </h2>
              <p className="font-default text-base leading-relaxed mb-10 max-w-md" style={{
              color: "var(--muted-foreground)"
            }}>
                Hier finden Sie Antworten auf die wichtigsten Fragen zu unseren Dienstleistungen. Für individuelle Anliegen stehen wir Ihnen jederzeit zur Verfügung.
              </p>
              <Link to="/kontakt" className="group inline-flex items-center gap-3 font-default text-xs uppercase tracking-[0.2em] text-foreground hover:text-primary transition-colors duration-300">
                <span className="border-b border-foreground/30 group-hover:border-primary pb-1 transition-colors duration-300">
                  Haben Sie weitere Fragen? Kontaktieren Sie uns
                </span>
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" style={{
                color: "var(--primary)"
              }} />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="border-t border-border">
              {faqs.map((faq, i) => {
              const isOpen = openIndex === i;
              const num = String(i + 1).padStart(2, "0");
              return <div key={i} data-index={i} className="border-b border-border">
                    <button type="button" onClick={() => setOpenIndex(isOpen ? null : i)} className="w-full py-7 flex items-start gap-5 md:gap-8 text-left group cursor-pointer" aria-expanded={isOpen}>
                      <span className={`font-default italic text-base md:text-lg tabular-nums shrink-0 pt-1 transition-all duration-400 ${isOpen ? "translate-x-0" : ""}`} style={{
                    color: isOpen ? "var(--primary)" : "var(--primary)",
                    opacity: isOpen ? 1 : 0.7
                  }}>
                        {num}
                      </span>
                      <span className="flex-1 font-default text-xl md:text-[1.35rem] leading-snug font-semibold tracking-tighter" style={{
                    color: "var(--foreground)"
                  }}>
                        {faq.question}
                      </span>
                      <span className="shrink-0 pt-1.5 transition-transform duration-400" style={{
                    color: "var(--primary)",
                    transform: isOpen ? "rotate(90deg)" : "rotate(0deg)"
                  }}>
                        {isOpen ? <X className="w-4 h-4" strokeWidth={1.25} /> : <Plus className="w-4 h-4" strokeWidth={1.25} />}
                      </span>
                    </button>

                    <div className="grid transition-all duration-[400ms] ease-out" style={{
                  gridTemplateRows: isOpen ? "1fr" : "0fr",
                  opacity: isOpen ? 1 : 0
                }}>
                      <div className="overflow-hidden">
                        <div className="pl-10 md:pl-14 pr-8 pb-8 max-w-2xl">
                          <p className="font-default text-base leading-relaxed" style={{
                        color: "var(--muted-foreground)"
                      }}>
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}